// Pure level resolution + formatting for the "Offene Tische" live overview (#205).
//
// Everything here is a plain function over plain data — no Angular, no DOM — so the behaviour that decides what
// colour a table is, and how long it has been idle, is unit-testable without a TestBed and cannot drift into a
// template.
//
// **Where the thresholds and colours come from.** NOT from this file: they are read off the *device configuration*
// (`idleWarningsEnabled` + `idleWarningLevels`, merged company ← shard — see
// `DeviceConfigHttpService.effectiveForShard`). That is deliberate: the MAUI register colours its own table-selection
// screen from the very same two keys, so a venue that moves its "too long ago" line from 30 to 20 minutes moves it
// on the register *and* on this screen at once. The constants below only mirror the platform catalog default
// (`CheqMe.Server.Contracts.Device.DeviceSettingCatalog`) so the screen still renders sensibly while the
// configuration request is in flight or has failed.

/**
 * One configured escalation level. Mirrors `DeviceSettingCatalog.IdleWarningLevelSchema` — the JSON keys are exactly
 * these names on the wire (the contract is serializer-agnostic by construction).
 */
export interface IdleWarningLevelConfig {
  /** Idle duration in **minutes** from which this level applies. Strictly ascending across the list. */
  thresholdMinutes: number;
  /** Short badge text (e.g. `Warn`). Shown in the map legend. */
  label: string;
  /** Foreground colour — the table number, the timer text, the warning badge and the legend dot. */
  textColor: string;
  /** Border colour of the level's marker / card chip. */
  borderColor: string;
  /** Background (surface) colour of the level's marker / timer pill. */
  surfaceColor: string;
}

/** Catalog key of the master switch. */
export const IDLE_WARNINGS_ENABLED_KEY = 'idleWarningsEnabled';

/** Catalog key of the ordered escalation levels. */
export const IDLE_WARNING_LEVELS_KEY = 'idleWarningLevels';

/**
 * The platform catalog's out-of-the-box levels — an amber "Warn" from 30 minutes and a red "Alert" from 60.
 *
 * Used only as the fallback while the shard-effective configuration is loading or unavailable, which is exactly
 * why it must be **byte-identical** to what the backend default-fills: a shard that has never touched the
 * setting must not see one set of colours from this fallback and another from the answer.
 *
 * > **Single source of truth: `CheqMe.Server.Contracts.Device/Configuration/DeviceSettingCatalog.cs`**
 * > (`Keys.IdleWarningLevels` → `IdleWarningLevelSchema.Default`). This constant is its mirror. Change one and
 * > you must change the other; `idle-warning-levels.spec.ts` pins the literals so the drift cannot be silent.
 *
 * Both pairs are the design handover's own colours and both clear WCAG AA as normal text:
 *
 * | Level | text on surface | ratio | replaced |
 * | --- | --- | --- | --- |
 * | Warn | `#8a6d12` on `#fdf6e3` | 4.55 : 1 | `#b8860b` on `#fdfaf3` (3.1 : 1) |
 * | Alert | `#c0392b` on `#fdecec` | 4.76 : 1 | `#d64545` on `#fdf5f5` (4.08 : 1) |
 *
 * `#d64545` was never an accessible *text* colour — the catalog had taken the mock's **badge/marker** tone for a
 * `textColor`. It remains the right badge tone; do not re-import it here. The borders (`#e0c675`, `#e5a0a0`) are
 * unchanged: WCAG 1.4.3 does not govern a decorative, non-text border.
 */
export const DEFAULT_IDLE_WARNING_LEVELS: readonly IdleWarningLevelConfig[] = [
  {thresholdMinutes: 30, label: 'Warn', textColor: '#8a6d12', borderColor: '#e0c675', surfaceColor: '#fdf6e3'},
  {thresholdMinutes: 60, label: 'Alert', textColor: '#c0392b', borderColor: '#e5a0a0', surfaceColor: '#fdecec'}
];

/**
 * The resolved presentation of one table's idle state: every colour the map marker and the list card need, already
 * chosen. Components never look at thresholds themselves — they render this.
 */
export interface IdleLevelAppearance {
  /** Index into the configured level list, or `-1` for "fresh" (below the lowest threshold / warnings off). */
  index: number;
  /** Whether this is the neutral, no-attention state. */
  fresh: boolean;
  /** The configured level's label, or `Aktiv` when fresh. */
  label: string;
  /** Marker fill. */
  markerSurface: string;
  /** Marker outline. */
  markerBorder: string;
  /** The big table number, on the marker and on the card. */
  numberColor: string;
  /** Fill of the little clock badge on the marker, or `null` when fresh (no badge is drawn at all). */
  badgeColor: string | null;
  /** Fill of the card's timer pill. */
  timerSurface: string;
  /** Text/icon colour inside the timer pill. */
  timerColor: string;
  /** The dot this level gets in the map legend. */
  legendDot: string;
}

/**
 * The neutral state — the handover's own "fresh" language: a white marker with a hairline border, near-black
 * number, and a green timer pill. It is intentionally *not* configurable: the configuration describes escalation,
 * and "nothing is wrong" is the absence of it.
 */
export const FRESH_APPEARANCE: IdleLevelAppearance = {
  index: -1,
  fresh: true,
  label: 'Aktiv',
  markerSurface: '#ffffff',
  markerBorder: '#d9d8d4',
  numberColor: '#1c1c1e',
  badgeColor: null,
  timerSurface: '#eef7f1',
  // The handover's #158a52 is 4.0:1 on this pill and fails WCAG AA at 13 px; one step darker clears it at 5.6:1
  // and is imperceptible side by side. (The legend dot below is a plain disc with no text on it, so it keeps the
  // handover's brighter green.)
  timerColor: '#0f7040',
  legendDot: '#12b76a'
};

/**
 * How a *free* table is drawn when the "freie Tische" toggle is on: present but explicitly quiet, so an open table
 * beside it still reads as the thing that needs attention. It carries no total and no badge.
 */
export const FREE_TABLE_APPEARANCE: IdleLevelAppearance = {
  index: -1,
  fresh: true,
  label: 'Frei',
  // Quiet, but as OPAQUE values rather than a translucent fill plus element opacity: a composited colour is one
  // a contrast check cannot see, and a free table's number still has to be readable from across the room.
  markerSurface: '#f2f1ee',
  markerBorder: '#dedcd8',
  numberColor: '#767570',
  badgeColor: null,
  timerSurface: '#f2f1ef',
  timerColor: '#8a8a8e',
  legendDot: '#d9d8d4'
};

/** Projects one configured level onto the colours the two views render. */
function appearanceOf(level: IdleWarningLevelConfig, index: number): IdleLevelAppearance {
  return {
    index,
    fresh: false,
    label: level.label,
    markerSurface: level.surfaceColor,
    markerBorder: level.borderColor,
    numberColor: level.textColor,
    // The badge is a solid disc with a white glyph, so it takes the level's DARKEST colour — the text colour.
    badgeColor: level.textColor,
    // One configured surface drives both the marker and the timer pill. The reference implementation used two
    // slightly different tints here (`#fdfaf3` marker vs `#fdf6e3` pill); collapsing them onto the single
    // configured `surfaceColor` is what keeps this screen and the register colour-identical, which is the point
    // of reading the colours from the configuration at all.
    timerSurface: level.surfaceColor,
    timerColor: level.textColor,
    // The legend dot keys the thing the user actually sees on a marker — the number, the timer text and the
    // badge — so it takes the same `textColor` those do. (`borderColor` is the pale outline tint; a legend of
    // pale straw and pale pink dots does not read as a colour key.)
    legendDot: level.textColor
  };
}

/**
 * Resolves the escalation level for an idle duration.
 *
 * @param idleMinutes Whole minutes since the last booking, or `null` when the reference has never been booked on
 *   (nothing to age — see {@link agoMin}).
 * @param levels The configured levels, ascending by threshold (see {@link readIdleWarningLevels}, which sorts).
 * @param enabled The `idleWarningsEnabled` master switch; `false` ⇒ everything renders fresh.
 */
export function resolveIdleLevel(
  idleMinutes: number | null,
  levels: readonly IdleWarningLevelConfig[],
  enabled: boolean
): IdleLevelAppearance {
  if (!enabled || idleMinutes === null) return FRESH_APPEARANCE;
  // Scanned from the END so the *highest* reached level wins, which is what the register does. `>=` on purpose:
  // a table that has sat idle for exactly the threshold has reached it.
  for (let index = levels.length - 1; index >= 0; index--) {
    if (idleMinutes >= levels[index].thresholdMinutes) return appearanceOf(levels[index], index);
  }
  return FRESH_APPEARANCE;
}

/** Whether a value is a `#RRGGBB` string — the only colour shape the backend's validator stores. */
function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value.trim());
}

/**
 * Reads the master switch out of an effective-configuration settings document.
 *
 * Anything that is not an explicit `false` counts as on: the catalog default is `true`, and a settings document
 * that predates the key (or failed to load) must not silently turn the warnings off.
 */
export function readIdleWarningsEnabled(settings: Record<string, unknown> | null | undefined): boolean {
  return settings?.[IDLE_WARNINGS_ENABLED_KEY] !== false;
}

/**
 * Reads the escalation levels out of an effective-configuration settings document, dropping malformed rows and
 * sorting what is left by threshold.
 *
 * Defensive on purpose. The value is an `objectlist` — a JSON array of loosely-typed maps that a misconfigured
 * layer can leave in any shape — and this screen must degrade to the catalog default rather than render a table
 * with `undefined` as its border colour.
 *
 * @returns The configured levels ascending by threshold, or {@link DEFAULT_IDLE_WARNING_LEVELS} when the document
 *   carries nothing usable. An explicitly **empty** list is honoured (a venue that wants no escalation at all
 *   configures it that way, and gets the same result as turning the master switch off).
 */
export function readIdleWarningLevels(
  settings: Record<string, unknown> | null | undefined
): readonly IdleWarningLevelConfig[] {
  const raw = settings?.[IDLE_WARNING_LEVELS_KEY];
  if (!Array.isArray(raw)) return DEFAULT_IDLE_WARNING_LEVELS;

  const levels = raw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item))
    .map(item => ({
      thresholdMinutes: Number(item['thresholdMinutes']),
      label: typeof item['label'] === 'string' ? item['label'] : '',
      textColor: isHexColor(item['textColor']) ? item['textColor'].trim() : '',
      borderColor: isHexColor(item['borderColor']) ? item['borderColor'].trim() : '',
      surfaceColor: isHexColor(item['surfaceColor']) ? item['surfaceColor'].trim() : ''
    }))
    .filter(level =>
      Number.isFinite(level.thresholdMinutes) && level.thresholdMinutes > 0 &&
      !!level.textColor && !!level.borderColor && !!level.surfaceColor)
    .sort((a, b) => a.thresholdMinutes - b.thresholdMinutes);

  // A document that carried the key but nothing valid in it is a *configuration* problem, not an intent to switch
  // escalation off — fall back rather than silently flatten every table to fresh. An explicitly empty array,
  // however, IS an intent, and `raw.length === 0` reaches this with `levels.length === 0` too, so distinguish.
  if (levels.length === 0 && raw.length > 0) return DEFAULT_IDLE_WARNING_LEVELS;
  return levels;
}

// region Formatting — the handover's helpers, one for one

/**
 * Whole minutes between `lastActivity` and `now`, never negative (a clock skewed a few seconds into the future
 * must read "0 Min", not "-1 Min").
 *
 * @returns `null` when there is no activity timestamp at all — the reference exists but has never been booked on,
 *   so there is nothing to age.
 */
export function agoMin(now: number, lastActivity: number | null): number | null {
  if (lastActivity === null || !Number.isFinite(lastActivity)) return null;
  return Math.max(0, Math.floor((now - lastActivity) / 60000));
}

/** `41 Min` / `1 Std 12 Min` / `2 Std` — the handover's `fmtDur`. `null` renders as an em dash. */
export function fmtDur(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 60) return `${minutes} Min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} Std ${rest} Min` : `${hours} Std`;
}

/**
 * `27,60 €` — the handover's `fmtEur`, but over **euros** rather than cents.
 *
 * The reference implementation stored integer cents; this system's `ICompositionData.singlePrice` is already a
 * decimal euro amount (the retiring screen piped it straight through Angular's `currency` pipe), so converting to
 * cents just to divide again would round twice. The rendered string is byte-identical either way.
 */
export function fmtEur(euros: number): string {
  const value = Number.isFinite(euros) ? euros : 0;
  return `${value.toFixed(2).replace('.', ',')} €`;
}

/** `23:17` — the handover's `fmtClock`, in the viewer's local time zone. */
export function fmtClock(timestamp: number | null): string {
  if (timestamp === null || !Number.isFinite(timestamp)) return '—';
  const date = new Date(timestamp);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * Sort key for a table name — the handover's `nameKey`: **pure numbers first, ascending**, then everything else
 * lexicographically. So `2, 3, 5, 8, 20, 21, 22, 31, B3`, which is the order a service reads a floor in; a plain
 * lexicographic sort would put `20` before `3` and a plain natural sort would interleave `B3` among the numbers.
 */
export function nameSortKey(name: string): [number, number | string] {
  const numeric = Number.parseInt(name, 10);
  // `parseInt('3a')` is 3, which would sort "3a" as a pure number — require the WHOLE string to be numeric.
  return /^\d+$/.test(name.trim()) && Number.isFinite(numeric) ? [0, numeric] : [1, name];
}

/** Compares two table names by {@link nameSortKey}. */
export function cmpName(a: string, b: string): number {
  const [groupA, valueA] = nameSortKey(a);
  const [groupB, valueB] = nameSortKey(b);
  if (groupA !== groupB) return groupA - groupB;
  if (typeof valueA === 'number' && typeof valueB === 'number') return valueA - valueB;
  return String(valueA).localeCompare(String(valueB), 'de', {sensitivity: 'base'});
}

// endregion
